import React, { useEffect, useRef, useState, useMemo } from 'react'
import { Route, Routes, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';

import { dateFormat } from '../../utils/formattedDate';
import { getUserCounts, angelListApi, suspendUserApi, restoreUserApi, updateUserDetailApi } from '../../api/usersApi';

import { PageTabs } from '../../components/PageTabs';
import Footer from '../../components/Footer';
import Header from '../../components/Header';
import HeaderInfo from '../../components/HeaderInfo';
import Notification from '../../components/Notification';
import AddNewAngel from '../../components/Angels/AddNewAngel';
import EditUser from '../../components/EditSlider/EditUser';
import ActiveAngels from './ActiveAngels';
import SuspendedAngels from './SuspendedAngels';
import NotCompletedAngels from './NotCompletedAngels';
import SingleSuspendPopup from '../../components/SingleSuspendPopup';
import MultipleSuspendPopup from '../../components/MultipleSuspendPopup';

import '../../assets/css/Angels.css'
import '../../assets/css/Popup.css'

const Angels = () => {
    const location = useLocation();
    const userEditRef = useRef();
    const addAngelRef = useRef();
    const suspendAngelRef = useRef();
    const suspendMultipleAngelRef = useRef();
    const debounceRef = useRef(null);

    const [angelCount, setAngelCount] = useState([]);
    const [activePage, setActivePage] = useState(`Active Angels (${angelCount[1]})`);
    const [ProfileTabs, setProfileTabs] = useState([]);
    const [menuActive, setMenuActive] = useState(false);
    const [showNotification, setShowNotification] = useState(false);
    const [notificationMessage, setNotificationMessage] = useState('');
    const [showAddNewAngelPopup, setShowAddNewAngelPopup] = useState(false);
    const [isEditAngel, setEditAngel] = useState(false)
    const [selectedAngel, setSelectAngel] = useState({})
    const [isSuspendLoading, setIsSuspendLoading] = useState(false);
    const [isLoading, setIsLoading] = useState(true)
    const [isLoadingData, setIsLoadingData] = useState(true);
    const [angelList, setAngelList] = useState([]);
    const [totalPage, setTotalPage] = useState(1);
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState('');
    const [showAngelSuspendPopup, setShowAngelSuspendPopup] = useState(false);
    const [showAngelMultipleSuspendPopup, setShowAngelMultipleSuspendPopup] = useState(false);
    const [selectedRows, setSelectedRows] = useState([]);
    const [isAllSelected, setIsAllSelected] = useState(false);
    const [toggleSelect, setToggleSelect] = useState(false);
    const [reason, setReason] = useState('');
    const [errorMessage, setErrorMessage] = useState('');
    const [personalEmail, setPersonalEmail] = useState(selectedAngel?.email || '')
    const [isSaveLoading, setIsSaveLoading] = useState(false);
    const [filterAngel, setFilterAngel] = useState({
        industry: [],
        startupStage: [],
        teamCount: [],
        plan: [],
        lastActiveDate: '',
        nextBillingDate: '',
        registedDate: '',
        suspendedDate: ''
    })
    const [addAngelErrorMessage, setAddAngelErrorMessage] = useState({
        name: '',
        email: '',
    })

    const currentStatus = useMemo(() => {
        const path = location.pathname.toLowerCase();
        if (path.includes('/notcompleted')) return 'notCompleted';
        if (path.includes('/suspended')) return 'suspended';
        return 'active';
    }, [location.pathname]);

    useEffect(() => {
        const statusLabelMap = {
            active: `Active Angels (${angelCount[1] || 0})`,
            notCompleted: `Not Completed (${angelCount[2] || 0})`,
            suspended: `Suspended (${angelCount[3] || 0})`
        };
        setActivePage(statusLabelMap[currentStatus]);

        setProfileTabs([
            { name: statusLabelMap['active'], href: '/angels/active' },
            { name: statusLabelMap['notCompleted'], href: '/angels/notcompleted' },
            { name: statusLabelMap['suspended'], href: '/angels/suspended' }
        ]);

        setSelectedRows([]);
        setIsAllSelected(false);
    }, [currentStatus, angelCount]);

    useEffect(() => {
        fetchAngelsCount();
    }, []);

    useEffect(() => {
        setIsLoading(true);
        setIsLoadingData(true);
        if (debounceRef?.current) {
            clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
            fetchAngels();
        }, 1200);

        return () => clearTimeout(debounceRef.current);
    }, [currentPage, location?.pathname, filterAngel, searchQuery]);

    useEffect(() => {
        setCurrentPage(1);
    }, [filterAngel, searchQuery]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (suspendAngelRef?.current && !suspendAngelRef?.current.contains(event.target)) {
                setShowAngelSuspendPopup(false);
            }
            if (suspendMultipleAngelRef?.current && !suspendMultipleAngelRef?.current.contains(event.target)) {
                setShowAngelMultipleSuspendPopup(false);
            }
            if (userEditRef?.current && !userEditRef?.current.contains(event.target)) {
                setEditAngel(false);
            }
            if (addAngelRef?.current && !addAngelRef?.current.contains(event.target)) {
                setShowAddNewAngelPopup(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);

        return () => {
            document.removeEventListener("mousedown", handleClickOutside);
        };
    }, []);

    const fetchAngelsCount = async () => {
        try {
            const res = await getUserCounts({ type: 2 });
            const countsByStatus = {
                1: 0,
                2: 0,
                3: 0,
            };
            res.forEach(item => {
                if (countsByStatus.hasOwnProperty(item?.status)) {
                    countsByStatus[item?.status] = item?.count;
                }
            });
            setAngelCount(countsByStatus);
        } catch (error) {
            console.log(error);
        }
    };

    const handleEditClick = async (row) => {
        try {
            setEditAngel(true);
            setSelectAngel(row);
        } catch (error) {
            console.error('Failed to fetch user detail on edit:', error);
        }
    };

    const angelApi = async () => {
        const status = currentStatus;

        const data = await angelListApi({
            status,
            page: currentPage || 1,
            search: searchQuery?.toLowerCase() || undefined,
            industry: status !== 'notCompleted' ? filterAngel?.industry?.map((item) => item?.toString()) : undefined,
            startupStage: status !== 'notCompleted' ? filterAngel?.startupStage?.map((item) => item?.toString()) : undefined,
            teamCount: status !== 'notCompleted' ? filterAngel?.teamCount?.map((item) => item?.toString()) : undefined,
            plan: status !== 'notCompleted' ? filterAngel?.plan?.map((item) => item?.toString()) : undefined,
            registeredStart: (filterAngel?.registedDate?.from !== '' && filterAngel?.registedDate?.from !== undefined && filterAngel?.registedDate?.from) ? dateFormat(filterAngel?.registedDate?.from) : undefined,
            registeredEnd: (filterAngel?.registedDate?.to !== '' && filterAngel?.registedDate?.to !== undefined && filterAngel?.registedDate?.to) ? dateFormat(filterAngel?.registedDate?.to) : undefined,
            suspendedStart: status !== 'notCompleted' ? ((filterAngel?.suspendedDate?.from !== '' && filterAngel?.suspendedDate?.from !== undefined && filterAngel?.suspendedDate?.from) ? dateFormat(filterAngel?.suspendedDate?.from) : undefined) : undefined,
            suspendedEnd: status !== 'notCompleted' ? ((filterAngel?.suspendedDate?.to !== '' && filterAngel?.suspendedDate?.to !== undefined && filterAngel?.suspendedDate?.to) ? dateFormat(filterAngel?.suspendedDate?.to) : undefined) : undefined,
        }, setIsLoadingData);
        return data
    }

    const fetchAngels = async () => {
        setIsLoading(false);
        try {
            setIsLoading(true)
            setIsLoadingData(true)

            const data = await angelApi()
            if (data?.list?.length <= 0) {
                setIsLoadingData(false)
            }
            setAngelList(data?.list);
            setTotalPage(data?.totalPage)
            setIsLoading(false)
        } catch (error) {
            console.log(error);
            setIsLoading(false)
            setIsLoadingData(false)
        }
    };

    const suspendUser = async (reason, userId, showAngelMultipleSuspendPopup = false) => {
        try {
            await suspendUserApi({
                reason,
                userIds: showAngelMultipleSuspendPopup ? userId : [userId],
            });
            return true;
        } catch (error) {
            console.error('API error:', error.response?.data || error.message);
            return false;
        }
    };

    const restoreUser = async (reason, userId, showAngelMultipleSuspendPopup = false) => {
        try {
            await restoreUserApi({
                reason,
                userIds: showAngelMultipleSuspendPopup ? userId : [userId],
            });
            return true;
        } catch (error) {
            console.error('API error:', error.response?.data || error.message);
            return false;
        }
    }

    const updateData = async (changedPersonalEmail) => {
        try {
            await updateUserDetailApi({
                userId: selectedAngel?.userId,
                data: {
                    email: changedPersonalEmail,
                },
            });
            return true;
        } catch (error) {
            console.log(error);
            return false;
        }
    }

    const handleUpdateUser = async (changedPersonalEmail) => {
        setIsSaveLoading(false)
        setShowNotification(false);
        try {
            setIsSaveLoading(true)
            const success = await updateData(changedPersonalEmail);
            if (success) {
                const data = await angelApi()
                if (data) {
                    setIsLoadingData(false)
                    setAngelList(data?.list);
                    setShowNotification(true);
                    setNotificationMessage('Your Change have been successfully saved.');
                    setTimeout(() => {
                        setShowNotification(false);
                    }, 8000);
                    setIsSaveLoading(false)
                    setEditAngel(false)
                }
                return true
            } else {
                setIsSaveLoading(false)
            }
        } catch (error) {
            console.log(error);
            setIsSaveLoading(false)
        }
    }

    const handleSuspendUser = async (reason, profileData) => {
        setIsSuspendLoading(false)
        setShowNotification(false);
        try {
            setIsSuspendLoading(true)
            const success = await suspendUser(reason, profileData?.userId);
            if (success) {
                const data = await angelApi()
                await fetchAngelsCount()
                if (data) {
                    setIsLoadingData(false)
                    setAngelList(data?.list);
                    setTotalPage(data?.totalPage)
                    setShowNotification(true);
                    setNotificationMessage(`${profileData?.name || ""} was suspended from Ivycorn.`);
                    setTimeout(() => {
                        setShowNotification(false);
                    }, 8000);
                    setIsSuspendLoading(false)
                    setEditAngel(false)
                    if (data?.list?.length === 0) {
                        setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)
                    }
                }
                return true
            } else {
                setIsSuspendLoading(false)
            }
        } catch (error) {
            console.log(error);
            setIsSuspendLoading(false)
        }
    }

    const handleActionUser = async (reason) => {
        let status = currentStatus;
        setShowNotification(false);
        setErrorMessage('');
        let notificationText = '';
        let success = false;
        if (!reason.trim()) {
            setErrorMessage(
                status !== "suspended"
                    ? "Please enter reason for suspension."
                    : "Please enter reason for restore."
            );
            return;
        }

        setIsSuspendLoading(true)
        if (status === "suspended") {
            if (showAngelSuspendPopup) {
                notificationText = `${selectedAngel?.name || ""} was restored from Ivycorn.`;
            } else {
                notificationText = `${selectedRows?.length} angels were restored from Ivycorn.`
            }
            success = await restoreUser(reason, showAngelSuspendPopup ? selectedAngel?.userId : selectedRows, showAngelMultipleSuspendPopup);
        } else {
            if (showAngelSuspendPopup) {
                notificationText = `${selectedAngel?.name || ""} was suspended from Ivycorn.`;
            } else {
                notificationText = `${selectedRows?.length} angels were suspended from Ivycorn.`
            }
            success = await suspendUser(reason, showAngelSuspendPopup ? selectedAngel?.userId : selectedRows, showAngelMultipleSuspendPopup);
        }
        if (success) {
            const data = await angelApi()
            await fetchAngelsCount()
            if (data) {
                setAngelList(data?.list);
                setTotalPage(data?.totalPage)
                setShowNotification(true);
                setNotificationMessage(notificationText);
                setToggleSelect(!toggleSelect);
                setTimeout(() => {
                    setShowNotification(false);
                }, 8000);
                setShowAngelMultipleSuspendPopup(false)
                setShowAngelSuspendPopup(false)
                setReason('')
                if (data?.list?.length === 0) {
                    setCurrentPage(currentPage > 1 ? currentPage - 1 : 1)
                }
            }
            setIsSuspendLoading(false)
        } else {
            setIsSuspendLoading(false)
        }
    };

    const LazyImage = ({ src, alt }) => {
        const [isVisible, setIsVisible] = useState(false);
        const imgRef = useRef();

        useEffect(() => {
            const observer = new IntersectionObserver(
                ([entry]) => {
                    if (entry.isIntersecting) {
                        setIsVisible(true);
                        observer.disconnect();
                    }
                },
                { threshold: 0.1 }
            );

            if (imgRef.current) {
                observer.observe(imgRef.current);
            }

            return () => {
                if (imgRef.current) {
                    observer.unobserve(imgRef.current);
                }
            };
        }, []);

        return (
            <img
                ref={imgRef}
                src={src}
                alt={alt}
                loading="lazy"
            />
        );
    };

    const angelUserList = useMemo(() => {
        return angelList
    }, [angelList])

    const angelProps = {
        setShowAddNewAngelPopup,
        setSelectAngel,
        isLoading,
        isLoadingData,
        angelList: angelUserList,
        totalPage,
        setEditAngel,
        setIsLoadingData,
        currentPage,
        setSearchQuery,
        searchQuery,
        setFilterAngel,
        filterAngel,
        setShowAngelSuspendPopup,
        setShowAngelMultipleSuspendPopup,
        selectedRows,
        setSelectedRows,
        isAllSelected,
        setIsAllSelected,
        toggleSelect,
        setErrorMessage,
        setCurrentPage,
        setReason,
        LazyImage,
        setAddAngelErrorMessage
    };

    const getAngelComponent = (Component) => <Component {...angelProps} />;

    return (
        <div className='main-wrapper'>
            <Header activePage="angels" menuActive={menuActive} setMenuActive={setMenuActive} />
            <div className="main-content">
                <div className={`content-wrapper`}>
                    <HeaderInfo title='Angels' description={
                        activePage.startsWith('Active')
                            ? `You have ${angelCount[1] || 0} active angels.`
                            : activePage.startsWith('Not Completed')
                                ? `You have ${angelCount[2] || 0} not completed angels.`
                                : activePage.startsWith('Suspended')
                                    ? `You have ${angelCount[3] || 0} suspended angels.`
                                    : ''
                    } />
                    <PageTabs tabs={ProfileTabs} activePage={activePage} setActivePage={(tabName) => {
                        setActivePage(tabName);
                        setFilterAngel({
                            industry: [],
                            startupStage: [],
                            teamCount: [],
                            plan: [],
                            lastActiveDate: '',
                            nextBillingDate: '',
                            registedDate: '',
                            suspendedDate: ''
                        });
                        setSearchQuery('')
                    }} />
                    <div className="angelsContent">
                        <Routes basename="/angels">
                            <Route path="/" element={getAngelComponent(ActiveAngels)} />
                            <Route path="/active" element={getAngelComponent(ActiveAngels)} />
                            <Route path="/notcompleted" element={getAngelComponent(NotCompletedAngels)} />
                            <Route path="/suspended" element={getAngelComponent(SuspendedAngels)} />
                        </Routes>
                    </div>
                </div>
                <Footer menuActive={menuActive} />
            </div>

            {
                showNotification &&
                <Notification showNotification={showNotification} notificationMessage={notificationMessage} setShowNotification={setShowNotification} />
            }
            {
                showAddNewAngelPopup &&
                <AddNewAngel
                    ref={addAngelRef}
                    isOpen={showAddNewAngelPopup}
                    onClose={() => setShowAddNewAngelPopup(false)}
                    setShowAddNewAngelPopup={setShowAddNewAngelPopup}
                    setShowNotification={setShowNotification}
                    setNotificationMessage={setNotificationMessage}
                    setAddAngelErrorMessage={setAddAngelErrorMessage}
                    addAngelErrorMessage={addAngelErrorMessage}
                />
            }
            {
                showAngelSuspendPopup &&
                <SingleSuspendPopup
                    isOpen={showAngelSuspendPopup}
                    onClose={() => { setShowAngelSuspendPopup(false) }}
                    title={`Are you sure you want to ${activePage == `Suspended (${angelCount[3] || 0})` ? 'restore' : 'suspend'} ${selectedAngel?.name}?`}
                    description={`${activePage === `Suspended (${angelCount[3] || 0})` ? "Restoring this angel will reinstate their access to Ivycorn, allowing them to continue using the service." : "Suspending this angel means they will be permanently suspended from using Ivycorn."}`}
                    ref={suspendAngelRef}
                    status={activePage === `Suspended (${angelCount[3] || 0})` && 'suspended'}
                    handleActionUser={handleActionUser}
                    reason={reason}
                    setReason={setReason}
                    errorMessage={errorMessage}
                    setErrorMessage={setErrorMessage}
                    isSuspendLoading={isSuspendLoading}
                />
            }
            {
                showAngelMultipleSuspendPopup &&
                <MultipleSuspendPopup
                    ref={suspendMultipleAngelRef}
                    isOpen={showAngelMultipleSuspendPopup}
                    onClose={() => { setShowAngelMultipleSuspendPopup(false) }}
                    title={`Are you sure you want to ${activePage === `Suspended (${angelCount[3] || 0})` ? 'restore' : 'suspend'} these ${selectedRows?.length} angels?`}
                    description={`${activePage === `Suspended (${angelCount[3] || 0})` ? "Restoring these angels will reinstate their access to Ivycorn, allowing them to continue using the service." : "Suspending these angels means they will be permanently suspended from using Ivycorn."}`}
                    handleActionUser={handleActionUser}
                    reason={reason}
                    setReason={setReason}
                    errorMessage={errorMessage}
                    setErrorMessage={setErrorMessage}
                    status={activePage === `Suspended (${angelCount[3] || 0})` && 'suspended'}
                    isSuspendLoading={isSuspendLoading}
                />
            }
            <EditUser
                setEdit={setEditAngel}
                ref={userEditRef}
                selectedUser={selectedAngel}
                isEditUser={isEditAngel}
                type='angel'
                handleActionUser={handleSuspendUser}
                isSuspendLoading={isSuspendLoading}
                setSelectUser={setSelectAngel}
                setPersonalEmail={setPersonalEmail}
                personalEmail={personalEmail}
                isSaveLoading={isSaveLoading}
                setIsSaveLoading={setIsSaveLoading}
                handleUpdateUser={handleUpdateUser}
                reason={reason}
                setReason={setReason}
            />
        </div>
    )
}

export default Angels
